// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { Icon } from '@iconify/react'

// ** Custom Components Imports
import { artifactsList } from 'src/data/artifacts'
import TablaReferencia from 'src/@core/components/table/TablaReferencia'
import { nFormatter } from 'src/@core/utils/numberFormatter'
import round from 'lodash/round'

/*
  Esta lista la pintaba un DataGrid con pageSize 15. Dos problemas medidos sobre
  out/artifacts/index.html:

    - DataGrid es virtualizado y no emite filas en el render del servidor.
    - Ademas la pestaña 'artifactsList' no era la activa por defecto, asi que ni
      llegaba a montarse: ninguno de los 35 artefactos aparecia en el HTML. Ni un
      nombre, de 8 que se buscaron.

  Con TablaReferencia y keepMounted en la pestaña (ver src/pages/artifacts), los 35
  artefactos con su calidad y sus estadisticas por estrella salen en el HTML.

  Se fue tambien un console.log(tabConf) que viajaba a produccion en cada carga.
*/

/**
 * Texto de una estadistica por estrella. La estrella 1 suma el crecimiento por
 * nivel (mainStatInc x maxLevel-1) porque es la que escala con el nivel del
 * artefacto; el resto son valores fijos.
 *
 * Estaba duplicado entre la columna y el filtro, con las dos copias calculando lo
 * mismo. Ahora hay una sola.
 */
const statsDeArtefacto = fila => {
  const { starsGeneral, mainStatInc, maxLevel } = fila

  return (starsGeneral ?? []).map(s => {
    const conNivel = s.stat + mainStatInc * (maxLevel - 1)

    const valor =
      s.star === 1
        ? Math.ceil(conNivel) > 999
          ? nFormatter(conNivel, 1)
          : `${round(conNivel, 1)}%`
        : Math.ceil(s.stat) > 999
          ? nFormatter(s.stat, 1)
          : `${s.stat}%`

    return `${'*'.repeat(s.star)}: ${s.statDesc} ${valor}`
  })
}

/*
  El texto de las stats se precalcula y se guarda en la fila: asi la columna lo
  muestra y el filtro lo busca sin recalcular 35 veces por tecla pulsada, y
  TablaReferencia puede filtrar por 'stats' como por cualquier otro campo.
*/
const filas = artifactsList.map(a => ({ ...a, stats: statsDeArtefacto(a).join(' | ') }))

const columnas = [
  {
    clave: 'desc',
    encabezado: 'Artifact',
    ordenable: true,
    formato: (v, fila) => (
      <>
        {v}
        <Typography variant='caption' component='span' sx={{ display: 'block', fontWeight: 400 }}>
          {fila.type}
        </Typography>
      </>
    )
  },
  { clave: 'quality', encabezado: 'Quality', ordenable: true, ancho: 90 },
  {
    clave: 'stats',
    encabezado: 'Stats',
    formato: (v, fila) => (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {statsDeArtefacto(fila).map(stat => (
          <Typography key={stat} variant='body2' sx={{ fontWeight: 600 }}>
            {stat}
          </Typography>
        ))}
      </Box>
    )
  }
]

const ArtifactList = ({ tabConf }) => {
  return (
    <Card>
      <Box
        sx={{
          marginBottom: '10px',
          marginTop: '10px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center'
        }}
      >
        <Typography variant='h5' component='p' color='primary' align='center'>
          <Icon fontSize={30} icon={tabConf['artifactsList'].icon} />
        </Typography>
        <Typography variant='h5' component='p' color='primary' align='center' sx={{ marginLeft: '5px' }}>
          {' ARTIFACTS LIST'}
        </Typography>
      </Box>
      <Divider />
      <TablaReferencia
        columnas={columnas}
        filas={filas}
        idFila='key'
        titulo='War and Order artifacts: quality and stat bonuses per star'
        clavesFiltro={['desc', 'type', 'quality', 'stats']}
      />
    </Card>
  )
}

export default ArtifactList
